import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';

export type RequestType = 'BALANCE_INCREASE' | 'EXTEND_EXPIRY' | 'RENEWAL' | 'SUPPORT' | 'PRODUCT_ACCESS';
export type RequestStatus = 'PENDING' | 'COMPLETED' | 'REJECTED';

export interface ClientRequestDto {
  id: string;
  clientId: string;
  clientName: string | null;
  requestedBy: string;
  requestType: RequestType;
  requestedProductCode: string | null;
  description: string;
  requestedBalanceAmount: number | null;
  requestedExpiryDate: string | null;
  currentBalance: number | null;
  currentExpiryDate: string | null;
  status: RequestStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  adminResponse: string | null;
  approvedBalanceAmount: number | null;
  approvedExpiryDate: string | null;
  clientAcknowledged: boolean;
  createdAt: string;
  modifiedAt: string | null;
}

export interface ClientRequestsPage {
  content: ClientRequestDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export async function fetchMyRequests(page = 0, size = 20): Promise<ClientRequestsPage> {
  const res = await api.get<ResponseEnvelope<ClientRequestsPage>>(Endpoints.me.requests, {
    params: { page, size },
  });
  return unwrap(res);
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await api.get<ResponseEnvelope<number>>(Endpoints.me.requestsUnreadCount);
  return unwrap(res);
}

export async function acknowledgeRequests(): Promise<void> {
  const res = await api.put<ResponseEnvelope<void>>(Endpoints.me.requestsAcknowledge);
  unwrap(res);
}

export async function createRequest(body: {
  requestType: RequestType;
  requestedProductCode?: string;
  description?: string;
  requestedBalanceAmount?: number;
  requestedExpiryDate?: string;
}): Promise<ClientRequestDto> {
  const res = await api.post<ResponseEnvelope<ClientRequestDto>>(Endpoints.me.requests, body);
  return unwrap(res);
}

export interface ProductCatalogItem {
  productCode: string;
  displayName: string;
  shortDescription: string;
  category: string;
  status: 'ACTIVE' | 'LOCKED';
}

export async function fetchMyProductCatalog(): Promise<ProductCatalogItem[]> {
  const res = await api.get<ResponseEnvelope<ProductCatalogItem[]>>(Endpoints.me.productCatalog);
  return unwrap(res);
}

export async function fetchAdminRequests(params?: {
  clientId?: string;
  requestType?: string;
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}): Promise<ClientRequestsPage> {
  const res = await api.get<ResponseEnvelope<ClientRequestsPage>>(Endpoints.adminRequests.list, {
    params,
  });
  return unwrap(res);
}

export async function fetchPendingCount(): Promise<number> {
  const res = await api.get<ResponseEnvelope<number>>(Endpoints.adminRequests.pendingCount);
  return unwrap(res);
}

export async function reviewRequest(
  id: string,
  body: {
    action: 'APPROVE' | 'REJECT';
    adminResponse?: string;
    approvedBalanceAmount?: number;
    approvedExpiryDate?: string;
  },
): Promise<ClientRequestDto> {
  const res = await api.put<ResponseEnvelope<ClientRequestDto>>(
    Endpoints.adminRequests.review(id),
    body,
  );
  return unwrap(res);
}
